import { Problem, ProblemDetectionContext, ProblemSeverity, ProblemType } from '../types';

export const detectFlowExecutionDelays = (context: ProblemDetectionContext, generateId: () => string): Problem[] => {
  const problems: Problem[] = [];

  if (!context.flowLogs || context.flowLogs.length < 2) {
    return problems;
  }

  const sortedEvents = [...context.flowLogs].sort(
    (a, b) => new Date(a.Timestamp).getTime() - new Date(b.Timestamp).getTime()
  );

  const ignoredBlocks = ['ShowView', 'PlayPrompt', 'GetUserInput'];

  for (let i = 1; i < sortedEvents.length; i++) {
    const currentEvent = sortedEvents[i];
    const previousEvent = sortedEvents[i - 1];

    if (
      ignoredBlocks.includes(previousEvent.ContactFlowModuleType) ||
      ignoredBlocks.includes(currentEvent.ContactFlowModuleType)
    ) {
      continue;
    }

    const currentTime = new Date(currentEvent.Timestamp).getTime();
    const previousTime = new Date(previousEvent.Timestamp).getTime();
    const delay = currentTime - previousTime;

    // Flag delays over 5 seconds between modules as potential issues
    const criticalDelayThreshold = 30000; // 30 seconds
    const warningDelayThreshold = 10000; // 10 seconds

    if (delay > criticalDelayThreshold) {
      const originalIndex = context.flowLogs!.indexOf(currentEvent);
      problems.push({
        id: generateId(),
        type: ProblemType.FLOW_EXECUTION_DELAY,
        severity: ProblemSeverity.CRITICAL,
        title: `Critical Flow Execution Delay`,
        description: `Excessive delay of ${Math.round(delay / 1000)} seconds between ${previousEvent.ContactFlowModuleType} and ${currentEvent.ContactFlowModuleType} modules`,
        timestamp: currentEvent.Timestamp,
        source: 'FLOW_LOG',
        relatedData: {
          delayMs: delay,
          delaySec: Math.round(delay / 1000),
          previousModule: {
            type: previousEvent.ContactFlowModuleType,
            identifier: previousEvent.Identifier,
            timestamp: previousEvent.Timestamp
          },
          currentModule: {
            type: currentEvent.ContactFlowModuleType,
            identifier: currentEvent.Identifier,
            timestamp: currentEvent.Timestamp
          }
        },
        suggestions: [
          'Check for resource bottlenecks or system performance issues',
          'Review Lambda function execution times if applicable',
          'Verify network connectivity and external service availability',
          'Consider optimizing flow logic to reduce processing time'
        ],
        index: originalIndex >= 0 ? originalIndex : undefined
      });
    } else if (delay > warningDelayThreshold) {
      const originalIndex = context.flowLogs!.indexOf(currentEvent);
      problems.push({
        id: generateId(),
        type: ProblemType.FLOW_EXECUTION_DELAY,
        severity: ProblemSeverity.MEDIUM,
        title: `Flow Execution Delay`,
        description: `Notable delay of ${Math.round(delay / 1000)} seconds between ${previousEvent.ContactFlowModuleType} and ${currentEvent.ContactFlowModuleType} modules`,
        timestamp: currentEvent.Timestamp,
        source: 'FLOW_LOG',
        relatedData: {
          delayMs: delay,
          delaySec: Math.round(delay / 1000),
          previousModule: {
            type: previousEvent.ContactFlowModuleType,
            identifier: previousEvent.Identifier,
            timestamp: previousEvent.Timestamp
          },
          currentModule: {
            type: currentEvent.ContactFlowModuleType,
            identifier: currentEvent.Identifier,
            timestamp: currentEvent.Timestamp
          }
        },
        suggestions: [
          'Monitor for performance trends over time',
          'Review module configuration for optimization opportunities',
          'Check external service response times'
        ],
        index: originalIndex >= 0 ? originalIndex : undefined
      });
    }
  }

  return problems;
};
